// Preview composer overlay. Renders all 5 §9.6 layers and the composed
// prompt (read-only) — exactly what the agent will see — without
// dispatching. Lets the operator audit the prompt before committing.
//
// The composed-prompt format is fixed (composedPrompt.ts) so the live
// composer matches this preview.

import React from 'react';

import { Button } from '../../../ui';
import { PRIOR_CALL_LAYERS, type PriorCallLayer } from './composedPrompt';

export interface ContactPreviewComposerModalProps {
  open: boolean;
  contactName: string;
  layerDetails: Record<PriorCallLayer['id'], string>;
  composedPrompt: string;
  onClose: () => void;
  /** "Looks good · Call now" hand-off. Closes preview, kicks the live composer. */
  onCallNow: () => void;
}

const ContactPreviewComposerModal: React.FC<ContactPreviewComposerModalProps> = ({
  open,
  contactName,
  layerDetails,
  composedPrompt,
  onClose,
  onCallNow,
}) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-composer-title"
    >
      <div
        className="w-full max-w-xl bg-surface-1 border border-white/[0.16] rounded-ds-xl p-6 shadow-ds-e3 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
          Preview · what the agent would see
        </div>
        <div
          id="preview-composer-title"
          className="text-lg font-semibold text-slate-50 mt-1 mb-4"
        >
          Composer preview · {contactName}
        </div>

        <ul>
          {PRIOR_CALL_LAYERS.map((layer) => (
            <li key={layer.id} className="flex items-center gap-2.5 py-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-ok shrink-0" />
              <span className="font-mono text-xs text-slate-400">{layer.id}</span>
              <span className="text-[13px] text-slate-50">{layer.label}</span>
              <span className="ml-auto font-mono text-[10px] text-ok-text">
                ready
              </span>
            </li>
          ))}
        </ul>

        <pre className="mt-4 max-h-[260px] overflow-auto bg-surface-2 rounded-ds-md p-3 text-[11px] text-slate-300 leading-relaxed font-mono whitespace-pre-wrap">
          {composedPrompt}
        </pre>

        {/* Layer detail summary — reinforces the composed prompt without forcing the operator to scan. */}
        <details className="mt-3">
          <summary className="cursor-pointer text-[11px] text-slate-400 font-mono">
            Per-layer detail
          </summary>
          <ul className="mt-2 space-y-1.5">
            {PRIOR_CALL_LAYERS.map((layer) => (
              <li key={layer.id} className="text-[11px] text-slate-300">
                <span className="font-mono text-slate-400">
                  {layer.id} · {layer.label}:
                </span>{' '}
                {layerDetails[layer.id]}
              </li>
            ))}
          </ul>
        </details>

        <div className="flex justify-end gap-2 mt-4">
          <Button intent="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button intent="primary" size="sm" onClick={onCallNow}>
            Looks good · Call now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContactPreviewComposerModal;
